/*
 * rewards-bar.js — SHAMTAM rewards ladder · DISPLAY-ONLY engine (v4).
 *
 * Shows free-shipping progress (slim sticky bar under the header) + a "surprise gift"
 * reward ladder (cart drawer / cart page). It reads the cart and PAINTS the promise —
 * it NEVER mutates the cart. No gift lines are added or removed on the storefront, and
 * there is no constant polling and no fetch-interception that schedules cart writes.
 *
 * Why: the previous design intercepted every fetch, polled /cart.js every 1.5s and
 * added/removed gift lines in parallel with the theme's own cart render. That hammering
 * tripped Shopify/Cloudflare throttling (cart endpoints then returned an HTML challenge
 * instead of JSON → "Unexpected token '<'") and raced the theme's renderer (items not
 * removed, wrong counts, phantom rows). A pure display engine removes that whole class
 * of bugs. The actual surprise gift (incense / candle / gemstone) is chosen by stock +
 * value and added to the order at fulfilment routing on the backend — so the storefront
 * never has to track gift inventory.
 *
 * Config: <script id="rewards-config" type="application/json">. Copy via [amount]/[gift] tokens.
 * Every-order window (Black Friday 2026, cfg.everyOrder): tier 1 at zero inside it, see everyOrderNow().
 */
(function () {
  'use strict';

  var cfgEl = document.getElementById('rewards-config');
  if (!cfgEl || window.__rewardsBarInit) return;
  window.__rewardsBarInit = true;

  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  // Copy now comes from {{ ... | t | json }}, and Shopify's `| t` HTML-escapes entities
  // (e.g. ' → &#39;, common once the Translate & Adapt app localises into fr/it/…). Decode
  // back to plain text so esc() doesn't double-escape it on display.
  (function () {
    function dec(s) { if (s == null) return s; var t = document.createElement('textarea'); t.innerHTML = String(s); return t.value; }
    if (cfg.labels) { for (var k in cfg.labels) if (typeof cfg.labels[k] === 'string') cfg.labels[k] = dec(cfg.labels[k]); }
    if (Array.isArray(cfg.gifts)) cfg.gifts.forEach(function (g) { if (g && typeof g.title === 'string') g.title = dec(g.title); });
  })();

  var currency = cfg.currency || 'GBP';
  var giftThresholds = String(cfg.giftThresholds || '').split(',').map(function (n) { return parseFloat(n); }).filter(function (n) { return !isNaN(n); });
  var gifts = Array.isArray(cfg.gifts) ? cfg.gifts.slice().sort(function (a, b) { return a.tier - b.tier; }) : [];
  var fsThreshold = (typeof cfg.freeShipThreshold === 'number') ? cfg.freeShipThreshold : null; // cents, or null
  var minOrder = (typeof cfg.minOrder === 'number') ? cfg.minOrder : 0; // cents — the cart's own block speaks below it
  var emptyPromise = (typeof cfg.emptyPromise === 'string') ? cfg.emptyPromise : '';
  var L = cfg.labels || {};
  // SHAMTAM Circle (T-CLUB-THEME): gifts may be for members only (shop rings.config.circleGifts = 'members'); a visitor
  // then sees each gift row locked with a join link. cfg.circle comes from snippets/rewards-bar.liquid.
  var C = cfg.circle || {};
  C.title = C.title || L.giftHeader || 'Your rewards';
  C.more = C.more || '{amount} more';
  // a visitor who joined through the pop-up (signed out) is a member too: the worker gives them the gift
  var popJoined = false; try { popJoined = !!localStorage.getItem('shamtam_pop_sub'); } catch (e) {}
  C.locked = !!(C.giftsMembers && !C.member && !popJoined);

  // Every-order window (Black Friday 2026, T-Q4-GREEN-FRIDAY): inside it tier 1 applies at ZERO — every
  // order gets the surprise incense — and tiers 2/3 keep their thresholds. The worker applies the same rule
  // at fulfilment (order-gift.js, GIFT_EVERY_ORDER_FROM/TO). Judged on the visitor's clock at every render,
  // never on Liquid's 'now', so a cached page still switches on and off on time. Dates come from the config.
  var eoFrom = NaN, eoTo = NaN;
  try { eoFrom = Date.parse(cfg.everyOrder && cfg.everyOrder.from); eoTo = Date.parse(cfg.everyOrder && cfg.everyOrder.to); } catch (e) {}
  function everyOrderNow() { var n = Date.now(); return isFinite(eoFrom) && isFinite(eoTo) && n >= eoFrom && n < eoTo; }

  var reachedCount = -1, timer = null;

  // ---------- helpers ----------
  function money(cents) {
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).format(cents / 100); }
    catch (e) { return (cents / 100).toFixed(2) + ' ' + currency; }
  }
  function moneyShort(cents) { return money(cents).replace(/[.,]00$/, ''); }
  function fill(tpl, map) { return String(tpl || '').replace(/[\[{](\w+)[\]}]/g, function (_, k) { return map[k] != null ? map[k] : ''; }); }
  function esc(s) { var d = document.createElement('div'); d.textContent = s == null ? '' : s; return d.innerHTML; }
  function icon(kind) {
    switch (kind) {
      case 'ship': return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 6.5h11v9h-11z"/><path d="M13.5 9.5h3.6l3.4 3.4v2.6h-7"/><circle cx="6.5" cy="17.5" r="1.8"/><circle cx="16.8" cy="17.5" r="1.8"/></svg>';
      case 'incense': return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M5.5 16.5h13l-1.6 4H7.1z" fill="currentColor" fill-opacity=".18"/><path d="M12 16.5V8.6"/><circle cx="12" cy="7.6" r="1.1" fill="currentColor" stroke="none"/><path class="rw-smoke" d="M12.2 5.6c-1.4-.9-.9-1.9-.1-2.6.7-.6.8-1.3.3-2"/></svg>';
      case 'candle': return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="8.5" y="10" width="7" height="10.5" rx="1.4"/><path d="M12 10V8"/><path class="rw-smoke" d="M12 2.8c1.5 1.6 1.5 3.6 0 4.6-1.5-1-1.5-3 0-4.6z" fill="currentColor" fill-opacity=".25"/><path d="M6.5 20.5h11"/></svg>';
      case 'gem': return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3h12l4 6-10 12L2 9z"/><path d="M11 3 8 9l4 12 4-12-3-6"/><path d="M2 9h20"/></svg>';
      case 'seal': return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l2.1 4.6L19 8.2l-3.6 3.3 1 4.9L12 14l-4.4 2.4 1-4.9L5 8.2l4.9-.6z"/></svg>';
      case 'check': return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6.5L9.2 17 4 11.8"/></svg>';
      case 'lock': return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>';
      case 'close': return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M5 5l14 14M19 5L5 19"/></svg>';
      default: return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12v8.5H4V12M2.5 7.5h19V12h-19zM12 20.5V7.5"/></svg>';
    }
  }

  // The bar icon sits in a ring that fills with the same progress as the rail under the bar (display-only).
  function ring(kind, pct) {
    return '<span class="rw-bar__ic rw-ring" style="--rw-p:' + Math.round(pct) + '"><span class="rw-ring__in">' + icon(kind) + '</span></span>';
  }

  function getCart() { return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(function (r) { return r.json(); }); }
  // qualifying spend = items total, excluding any legacy gift line still in a cart
  function eligibleSpend(cart) {
    return cart.items.reduce(function (s, it) {
      return (it.properties && it.properties._gift_tier) ? s : s + it.final_line_price;
    }, 0);
  }

  // sticky bar can be dismissed for the session (returns next visit, never lost for good)
  var BAR_DISMISS_KEY = 'shamtam:rwbar-dismissed';
  function barDismissed() { try { return sessionStorage.getItem(BAR_DISMISS_KEY) === '1'; } catch (e) { return false; } }

  // ---------- model ----------
  function milestones(spend) {
    var ms = [];
    if (fsThreshold != null) ms.push({ kind: 'ship', threshold: fsThreshold, label: L.freeShip || 'Free shipping' });
    var weekend = everyOrderNow();
    for (var i = 0; i < giftThresholds.length && i < gifts.length; i++) {
      ms.push({ kind: 'gift', icon: gifts[i].icon, threshold: (weekend && i === 0) ? 0 : Math.round(giftThresholds[i] * 100), label: gifts[i].title, locked: C.locked });
    }
    ms.sort(function (a, b) { return a.threshold - b.threshold; });
    // a members-only gift is never 'reached' for a visitor: the row stays locked and the next line invites them in
    ms.forEach(function (m) { m.reached = spend >= m.threshold && !m.locked; });
    return ms;
  }
  function nextReward(ms) { for (var i = 0; i < ms.length; i++) { if (!ms[i].reached) return ms[i]; } return null; }
  function nextPct(ms, spend) {
    for (var i = 0; i < ms.length; i++) {
      if (spend < ms[i].threshold) { var lo = i ? ms[i - 1].threshold : 0; return Math.max(0, Math.min(100, ((spend - lo) / (ms[i].threshold - lo)) * 100)); }
    }
    return 100;
  }
  function nextLine(ms, spend) {
    var nx = nextReward(ms);
    if (!nx) return esc(L.allUnlocked || 'You have unlocked every reward!');
    if (nx.locked && spend >= nx.threshold) return esc(C.joinNote || C.members || '');
    var tpl = nx.kind === 'ship' ? (L.freeShipNext || 'Spend [amount] more for free shipping') : (L.giftNext || 'Spend [amount] more for a [gift]');
    if (nx.locked && C.members) tpl += ' · ' + esc(C.members);
    return fill(tpl, { amount: '<b>' + money(Math.max(0, nx.threshold - spend)) + '</b>', gift: '<b>' + esc(nx.label) + '</b>' });
  }

  // ---------- render ----------
  function render(cart, spend) {
    var ms = milestones(spend);
    renderBar(ms, spend, cart.item_count === 0);
    renderLadder(ms, spend, cart.item_count === 0);
    var nowReached = ms.filter(function (m) { return m.reached; }).length;
    if (reachedCount >= 0 && nowReached > reachedCount) setTimeout(celebrate, 140);
    reachedCount = nowReached;
  }

  function renderBar(ms, spend, empty) {
    var el = document.querySelector('[data-rewards-bar]');
    if (!el) return;
    if (barDismissed() || !ms.length) { el.hidden = true; return; }
    // Empty cart (2026-09-17): the promise, not a countdown. "Spend £33 more" made no sense to a
    // visitor who had not put anything in the bag yet; the delivery terms do.
    if (empty && emptyPromise) {
      el.hidden = false;
      el.classList.add('rw-bar--promise');
      el.innerHTML = '<div class="rw-bar__inner">' + ring('ship', 0) + '<span class="rw-bar__msg">' + esc(emptyPromise) + '</span></div>' +
        '<button class="rw-bar__close" type="button" data-rw-close aria-label="' + esc(L.dismiss || 'Close') + '">' + icon('close') + '</button>';
      return;
    }
    el.classList.remove('rw-bar--promise');
    // The bar only ever says how much is left to the next reward (Alex 2026-10-26): the next threshold not yet spent,
    // members-only or not. Past the last one: "all unlocked" for a member, nothing for a visitor (the cart says why).
    var nx = null, inner;
    for (var i = 0; i < ms.length; i++) { if (spend < ms[i].threshold) { nx = ms[i]; break; } }
    if (!nx) {
      if (C.locked) { el.hidden = true; return; }
      inner = ring('check', 100) + '<span class="rw-bar__msg">' + esc(L.allUnlocked || 'You have unlocked every reward!') + '</span>';
    } else {
      var tpl = nx.kind === 'ship' ? (L.freeShipNext || 'Spend [amount] more for free shipping') : (L.giftNext || 'Spend [amount] more for a [gift]');
      inner = ring(nx.kind === 'ship' ? 'ship' : (nx.icon || 'gift'), nextPct(ms, spend)) + '<span class="rw-bar__msg">' +
        fill(tpl, { amount: '<b>' + money(Math.max(0, nx.threshold - spend)) + '</b>', gift: '<b>' + esc(nx.label) + '</b>' }) + '</span>';
    }
    el.hidden = false;
    el.innerHTML = '<div class="rw-bar__inner">' + inner + '</div>' +
      '<button class="rw-bar__close" type="button" data-rw-close aria-label="' + esc(L.dismiss || 'Close') + '">' + icon('close') + '</button>' +
      '<span class="rw-bar__rail"><span class="rw-bar__fill" style="width:' + nextPct(ms, spend) + '%"></span></span>';
  }

  // ONE ladder for every reward of this order (Alex 2026-10-26, T-CLUB-THEME): free delivery and the three surprise
  // gifts on one rail, each row saying done / how much more / for Circle members. It replaced the one-threshold card
  // (2026-09-17), which hid the gifts until free delivery was reached. Below the minimum order the cart's own block is
  // still the only message. The Circle cashback for the order is its own line next to checkout (shamtam-rings-cart).
  function renderLadder(ms, spend, empty) {
    var el = document.querySelector('[data-rewards-ladder]');
    if (!el) return;
    if (empty || !ms.length) { el.hidden = true; return; }
    if (minOrder && spend < minOrder) { el.hidden = true; return; }
    var weekend = everyOrderNow();
    var max = ms[ms.length - 1].threshold || 1;
    var fillW = Math.max(0, Math.min(100, (spend / max) * 100));
    var nx = nextReward(ms);
    var head = nextLine(ms, spend);
    var headIsJoin = C.locked && C.joinNote && head === esc(C.joinNote);
    var headHtml = headIsJoin ? '<a href="' + esc(C.joinUrl || '#') + '" data-circle-join>' + head + '</a>' : head;
    var title = weekend ? (L.giftHeaderEvery || C.title) : C.title;
    var marks = ms.map(function (m) {
      var x = Math.max(0, Math.min(100, (m.threshold / max) * 100));
      return '<i class="rw-mark' + (m.reached ? ' is-on' : '') + '" style="left:' + x.toFixed(1) + '%"></i>';
    }).join('');
    var rows = ms.map(function (m) {
      var name = m.kind === 'ship' ? (m.label || 'Free delivery') : fill(L.giftCardTitle || 'A [gift]', { gift: m.label });
      var state;
      if (m.reached) state = '<span class="rw-row__st is-on">' + icon('check') + '</span>';
      else if (m.locked) state = '<a class="rw-row__st rw-row__join" href="' + esc(C.joinUrl || '#') + '" data-circle-join>' + icon('lock') + '<span>' + esc(C.members) + '</span></a>';
      else state = '<span class="rw-row__st">' + esc(fill(C.more, { amount: money(Math.max(0, m.threshold - spend)) })) + '</span>';
      var amt = m.threshold > 0 ? moneyShort(m.threshold) : esc(C.every || '');
      return '<li class="rw-row' + (m.reached ? ' is-reached' : '') + (m === nx ? ' is-next' : '') + (m.locked ? ' is-locked' : '') + '">' +
        '<span class="rw-row__ic">' + icon(m.kind === 'ship' ? 'ship' : (m.icon || 'gift')) + '</span>' +
        '<span class="rw-row__name">' + esc(name) + '</span>' +
        '<span class="rw-row__amt">' + amt + '</span>' + state + '</li>';
    }).join('');
    el.hidden = false;
    el.innerHTML =
      '<div class="rw-lad__top"><span class="rw-lad__seal">' + icon('seal') + '</span>' +
        '<span class="rw-lad__title">' + esc(title) + '</span></div>' +
      '<div class="rw-lad__head">' + headHtml + '</div>' +
      '<div class="rw-lad__rail rw-lad__rail--marks"><span class="rw-lad__fill" style="width:' + fillW.toFixed(1) + '%"></span>' + marks + '</div>' +
      '<ul class="rw-rows">' + rows + '</ul>' +
      (C.locked && C.joinNote && !headIsJoin ? '<p class="rw-lad__foot"><a href="' + esc(C.joinUrl || '#') + '" data-circle-join>' + esc(C.joinNote) + '</a></p>' : '');
  }

  // small, brand-coloured confetti burst at the most recently unlocked card
  function celebrate() {
    try {
      var done = document.querySelectorAll('[data-rewards-ladder] .rw-card.is-reached');
      var anchor = done.length ? done[done.length - 1] : (document.querySelector('[data-rewards-ladder]') || document.querySelector('[data-rewards-bar]'));
      if (!anchor) return;
      var r = anchor.getBoundingClientRect();
      if (!r.width && !r.height) return;
      var box = document.createElement('div');
      box.className = 'rw-confetti';
      box.style.left = (r.left + r.width / 2) + 'px';
      box.style.top = (r.top + r.height / 2) + 'px';
      var colors = ['#748c6a', '#e0a45f', '#8a9b78', '#f0e4d4', '#5d7055'];
      for (var i = 0; i < 18; i++) {
        var p = document.createElement('i');
        var ang = Math.random() * Math.PI * 2, dist = 45 + Math.random() * 75;
        p.style.setProperty('--dx', Math.round(Math.cos(ang) * dist) + 'px');
        p.style.setProperty('--dy', Math.round(Math.sin(ang) * dist - 25) + 'px');
        p.style.setProperty('--rot', Math.round(Math.random() * 420 - 210) + 'deg');
        p.style.background = colors[i % colors.length];
        p.style.animationDelay = (Math.random() * 0.07).toFixed(2) + 's';
        box.appendChild(p);
      }
      document.body.appendChild(box);
      setTimeout(function () { if (box.parentNode) box.parentNode.removeChild(box); }, 1300);
    } catch (e) {}
  }

  // ---------- sync (READ-ONLY — the theme owns every cart mutation) ----------
  // the worker judges gifts on the subtotal after order-level discounts: never show a tier the order will not get
  function refresh() { return getCart().then(function (cart) { var sp = eligibleSpend(cart); if (typeof cart.total_price === 'number') sp = Math.min(sp, cart.total_price); render(cart, sp); }).catch(function () {}); }
  function schedule() { if (timer) clearTimeout(timer); timer = setTimeout(refresh, 180); }

  // dismiss the sticky bar (delegated — survives innerHTML rebuilds)
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-rw-close]');
    if (!btn) return;
    try { sessionStorage.setItem(BAR_DISMISS_KEY, '1'); } catch (e2) {}
    var el = document.querySelector('[data-rewards-bar]'); if (el) el.hidden = true;
  });

  // re-sync on the theme's own cart events (this theme dispatches all of these on
  // add / qty-change / remove / drawer-open / bfcache-restore). No polling, no writes.
  ['cart:updated', 'cart:refresh', 'cart:change', 'cart:build', 'on:cart:add', 'on:line-item:change',
   'dispatch:cart-drawer:open', 'on:cart-drawer:after-open', 'on:bfcache:load-restore'].forEach(function (ev) {
    document.addEventListener(ev, schedule);
  });

  if (document.readyState !== 'loading') refresh(); else document.addEventListener('DOMContentLoaded', refresh);
})();
